import { date2usDotDateTime, date2usSlashDateTime } from 'helpers';
import styles from './CheckInDetails.module.scss';
import { CheckInItem } from 'types';
import { ReactComponent as ExpandIcon } from "assets/expand.svg";
import { ReactComponent as CameraIcon } from "assets/camera.svg";
import SelectComponent from 'components/Input/Select';
import { checkInTypeOptions } from '../../variables';
import React from 'react';
import Button from 'components/Button/Button';
import Toastr from 'components/Toastr';
import useExternalApiService from 'helpers/ExternalApiService';
import { useLocalStorageState } from 'hooks/useLocalStorageState';
import { store } from 'stores/Store';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { Image, Modal } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import '@mantine/carousel/styles.css';

interface CheckInDetailsProps {
  checkIn: CheckInItem;
  returnToCheckInList: () => void;
  onCheckInUpdate?: (updatedCheckIn: CheckInItem) => void;
}

const CheckInDetails = ({ checkIn, returnToCheckInList, onCheckInUpdate }: CheckInDetailsProps) => {
  const [selectedCheckInType, setSelectedCheckInType] = React.useState<any>(
    {value: checkIn.type, label: checkInTypeOptions.find(opt => opt.value === checkIn.type)?.label}
  );
  
  // Use localStorage to persist notes while editing
  const [notes, setNotes] = useLocalStorageState<string>(
    `temp_notes_${checkIn.id}`, 
    checkIn.notes
  );
  
  const [isDirty, setIsDirty] = React.useState(false);
  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [showPhotoModal, setShowPhotoModal] = React.useState(false);
  const [showCamera, setShowCamera] = React.useState(false);
  const [capturedImage, setCapturedImage] = React.useState<string | null>(null);
  const [showCarouselModal, setShowCarouselModal] = React.useState(false);
  const [carouselIndex, setCarouselIndex] = React.useState(0);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isLoadingPhotos, setIsLoadingPhotos] = React.useState(true);
  // Mock photos data - replace with actual photos from checkIn
  const [mockPhotos, setMockPhotos] = React.useState<{id: number | string, url: string, api_url?: string, imgObject?: HTMLImageElement, dataUrl?: string}[]>([]);

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData } = useExternalApiService(apiUrl);

  React.useEffect(() => {
    const fetchImages = async () => {
      try {
        setIsLoadingPhotos(true);
        const result = await postData('/api/get_images_from_azure', {
          fmc_id: store.user?.userName || '',
          practitioner_id: checkIn.practitioner_id,
          checkin_id: checkIn.id,
        });

        if (Array.isArray(result?.images)) {
          // Preload each image and convert to data URL for offline use
          const loadImage = (url: string) =>
            new Promise<{img: HTMLImageElement, dataUrl: string}>((resolve, reject) => {
              const img = new window.Image();
              img.crossOrigin = 'anonymous'; // Enable CORS for canvas operations
              img.src = url;
              img.onload = () => {
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');
                if (ctx) {
                  canvas.width = img.width;
                  canvas.height = img.height;
                  ctx.drawImage(img, 0, 0);
                  const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
                  resolve({img, dataUrl});
                } else {
                  reject(new Error('Could not get canvas context'));
                }
              };
              img.onerror = reject;
            });
  
          const loadedImages = await Promise.all(
            result.images.map((url: string, idx: number) =>
              loadImage(url).then(({img, dataUrl}) => ({
                id: idx + 1,
                url: dataUrl, 
                api_url: url, 
                imgObject: img,
                dataUrl: dataUrl, 
              }))
            )
          );
          setMockPhotos(loadedImages);
        }
      } catch (error) {
        console.error('Failed to fetch images from Azure:', error);
      } finally {
        setIsLoadingPhotos(false);
      }
    };
    fetchImages();
  }, [checkIn.practitioner_id, checkIn.id]);

  React.useEffect(() => {
    // Check if there are stored notes - only run when notes are loaded
    if (notes && notes !== checkIn.notes) {
      setIsDirty(true);
      setToastMessage('You have unsaved changes');
    }
  }, [notes, checkIn.notes]); // Run when notes are loaded and when checkIn.notes changes

  const handleShowToast = (message: string) => {
    setShowToast(true);
    setToastMessage(message);
  };

  const updateCheckInNotes = async (notes: string) => {
    try {
      const result = await postData('/api/update_checkin', {
        checkin_id: checkIn.id.toString(),
        checkin_data: {
          notes: notes
        }
      });
      
      // Update the check-in in the parent component if callback is provided
      if (onCheckInUpdate) {
        onCheckInUpdate({
          ...checkIn,
          notes: notes,
          updated_at: new Date().toISOString()
        });
      }
      
      // Clear store after successful save
      store.clearTemporaryNotes(`temp_notes_${checkIn.id}`);
      setIsDirty(false);
      handleShowToast('Check-In Notes Saved');
    } catch (error) {
      console.error('Error updating check-in notes:', error);
      handleShowToast('Error saving check-in notes');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    await updateCheckInNotes(notes || '');
  };

  const handleDeletePhoto = async () => {
    console.log("handleDeletePhoto", carouselIndex);
    
    const photoToDelete = mockPhotos[carouselIndex];
    if (!photoToDelete) return;
    
    try {
      // Call API to delete image from Azure
      await postData('/api/delete_image_from_azure', {
        fmc_id: store.user?.userName || '',
        practitioner_id: checkIn.practitioner_id,
        checkin_id: checkIn.id,
        image_url: photoToDelete.api_url || photoToDelete.url
      });
      
      // Remove photo from local state using the photo's unique ID
      const remainingPhotos = mockPhotos.filter((_, i) => i !== carouselIndex);
      setMockPhotos(remainingPhotos);
      
      // Close modal if no photos left
      if (remainingPhotos.length === 0) {
        setShowCarouselModal(false);
      } else {
        // Adjust carousel index if needed
        if (carouselIndex >= remainingPhotos.length) {
          setCarouselIndex(remainingPhotos.length - 1);
        }
      }
      
      handleShowToast('Photo deleted successfully');
    } catch (error) {
      console.error('Failed to delete photo:', error);
      handleShowToast('Failed to delete photo');
    }
  };

  const handleUndo = () => {
    setIsDirty(false);
    setNotes(checkIn.notes);
    store.clearTemporaryNotes(`temp_notes_${checkIn.id}`);
    handleShowToast('Notes Restored');
  };

  const onChangeNotes = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newNotes = e.target.value;
    setNotes(newNotes);
    
    const hasChanges = newNotes !== checkIn.notes;
    setIsDirty(hasChanges);
    
    if (hasChanges) {
      setShowToast(true);
      setToastMessage('You have unsaved changes');
    }
  };

  const isIOS = () => {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
           (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1);
  };

  const handlePhotoUpload = () => {
    if (isIOS()) {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    } else {
      setShowPhotoModal(true);
    }
  };

  const handlePhotoClick = (index: number) => {
    
    // Only open carousel if we have photos and they're loaded
    if (mockPhotos.length > 0 && !isLoadingPhotos) {
      setCarouselIndex(index);
      setShowCarouselModal(true);
    }
  };

  const handleGallerySelect = () => {
    setShowPhotoModal(false);
    setShowCamera(false);
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleCameraSelect = async () => {
    setShowPhotoModal(false);
    setCapturedImage(null);
    setShowCamera(true);

    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        // Mobile-optimized camera constraints
        const constraints = {
          video: {
            facingMode: 'environment', // Use back camera on mobile
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      } catch (err) {
        console.error("Cannot access camera, fallback to file input", err);
        handleShowToast('Camera access denied. Using gallery instead.');
        setShowCamera(false);
        if (fileInputRef.current) {
          fileInputRef.current.click();
        }
      }
    } else {
      console.warn("MediaDevices not supported, fallback to file input");
      handleShowToast('Camera not supported. Using gallery instead.');
      setShowCamera(false);
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  };

  const takePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageUrl = canvas.toDataURL("image/png");
      
      const stream = video.srcObject as MediaStream;
      stream?.getTracks().forEach((track) => track.stop());
      setShowCamera(false);

      await addPhotoToList(imageUrl);
      console.log("Captured image:", imageUrl);
    }
  };
  
  const uploadImageToAPI = async (imageUrl: string) => {
    try {
      // Step 1: Convert base64 Data URL to Blob
      const res = await fetch(imageUrl);
      const blob = await res.blob();
  
      // Step 2: Request SAS URL from backend
      const sasResponse = await postData('/api/get_azure_sas', {
        fmc_id: store.user?.userName || '',
        practitioner_id: checkIn.practitioner_id,
        checkin_id: checkIn.id,
        filename: `checkin_${Date.now()}.jpg` // optional unique name
      });
  
      const { blob_url } = sasResponse;
  
      // Step 3: Upload the Blob directly to Azure
      const headers = new Headers();
      headers.append('x-ms-blob-type', 'BlockBlob'); // required
  
      const uploadResponse = await fetch(blob_url, {
        method: 'PUT',
        headers,
        body: blob
      });
  
      if (!uploadResponse.ok) {
        throw new Error(`Azure upload failed with status ${uploadResponse.status}`);
      }

      // Return success response
      return { success: true, blob_url };
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    }
  };
  
  const addPhotoToList = async (imageUrl: string) => {
    try {
      // Check photo limit
      if (mockPhotos.length >= 10) {
        handleShowToast('Maximum of 10 photos allowed');
        return;
      }
      
      handleShowToast('Uploading photo...');
      
      const uploadResult = await uploadImageToAPI(imageUrl);
      
      const newPhoto = {
        id: mockPhotos.length + 1,
        url: imageUrl, 
        api_url: uploadResult.blob_url || imageUrl,
        dataUrl: imageUrl 
      };
      setMockPhotos(prev => [...prev, newPhoto]);
      setCapturedImage(null);
      
      handleShowToast('Photo uploaded successfully');
    } catch (error) {
      console.error('Failed to upload photo:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to upload photo';
      if (error instanceof Error) {
        if (error.message.includes('SAS URL')) {
          errorMessage = 'Failed to get upload permission';
        } else if (error.message.includes('Azure upload failed')) {
          errorMessage = 'Failed to upload to cloud storage';
        } else if (error.message.includes('Invalid image URL')) {
          errorMessage = 'Invalid image format';
        } else {
          errorMessage = `Upload failed: ${error.message}`;
        }
      }
      
      handleShowToast(errorMessage);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      await addPhotoToList(url);
      console.log("Gallery file:", file);
    }
    e.target.value = '';
  };

  return <div className={styles.wrapper}>
    <div>
        {checkIn.verified ? (
          <div className={styles.verifiedWrapper}>
            <p className={styles.verifiedText}>Verified Check-In</p>
          </div>
        ) : (
          <div className={styles.unverifiedWrapper}>
            <p className={styles.unverifiedText}>Unverified Check-In</p>
          </div>
        )}
    </div>
    <div className={styles.headerWrapper} >      
      <div className={styles.iconWrapper} onClick={returnToCheckInList}><ExpandIcon style={{rotate: '180deg'}} /></div>      
      {/*<p className={styles.header}>{date2usSlashDateTime(checkIn.created_at)}</p>*/}
      <p className={styles.header}>
          {date2usSlashDateTime(
              checkIn.created_at,
              checkIn.is_archived
                ? 'GMT'
                : checkIn.is_archived_bg
                ? 'America/New_York'
                : undefined
            )}
        </p>
    </div>    
    <div className={styles.fmcWrapper}>
      <div className={styles.fmcName}>{checkIn.user_name}</div>
      {/* 
      <SelectComponent 
        options={checkInTypeOptions} 
        placeholder='Select a Type' 
        value={selectedCheckInType} 
        onChange={(e: any) => setSelectedCheckInType(e)}                
      />      
      */}
      <div className={styles.readOnlySelect}>
        {selectedCheckInType?.label || "—"}
      </div>
    </div>  
    {/* Blue badge pill warning message */}
    {(checkIn.is_archived) && (
      <div className={styles.badgerImportPill}>
        IMPORTED FROM BADGER & NETSUITE
      </div>
    )}  
    {(checkIn.is_archived_bg) && (
      <div className={styles.badgerImportPill}>
        IMPORTED FROM BADGER
      </div>
    )}      
    <div className={styles.headerRow}>            
      <p className={styles.label}>Meeting Notes</p>  
      {/* Save/Undo buttons */}
      {isDirty && (
        <div className={styles.buttonsWrapper}>
          <Button type="outline" onClick={handleUndo}>UNDO</Button>
          <Button 
            textColor="white" 
            color="#00DC4980" 
            borderColor="#00DC49CC" 
            type="outline" 
            onClick={handleSave}
          >
            {isSaving ? 'SAVING...' : 'SAVE'}
          </Button>
        </div>
      )}      
    </div>    

    {/* Editable notes textarea */}       

    <textarea
      value={notes}
      onChange={onChangeNotes}
      className={styles.notesTextarea}
      placeholder="Enter your notes here..."
    />
    
    <div className={styles.headerRow}>
      <p style={{fontWeight: '800'}} className={styles.label}>Photos</p>
      <div className={styles.label}>You can upload up to 10 photos</div>
    </div>

    <div className={styles.photoUploadSection}>
      <div className={styles.photosGrid}>
        {/* Loading placeholder while fetching images */}
        {isLoadingPhotos && (
          <div className={styles.photoLoadingContainer}>
            <div className={styles.photoLoadingPlaceholder}>
              <div className={styles.loadingSpinner}></div>
              <span className={styles.loadingText}>Loading photos...</span>
            </div>
          </div>
        )}
        
        {/* Show upload button and photos only when not loading */}
        {!isLoadingPhotos && (
          <>
            {mockPhotos.length < 10 && (
              <div className={styles.photoUploadCard} onClick={handlePhotoUpload}>
                <div className={styles.cameraIcon}>
                  <CameraIcon />
                </div>
                <div className={styles.uploadText}>Add Check-In Photo</div>
              </div>
            )}
            
            {mockPhotos.map((photo, index) => (
              <div key={photo.id} className={styles.photoCard} onClick={() => handlePhotoClick(index)}>
                <img 
                  src={photo.dataUrl || photo.url} 
                  alt={`Photo ${photo.id}`} 
                  className={styles.photoImage}
                  onError={(e) => {
                    // Fallback to original URL if data URL fails
                    const target = e.target as HTMLImageElement;
                    if (photo.api_url && target.src !== photo.api_url) {
                      target.src = photo.api_url;
                    } else {
                      // Fallback to placeholder if all else fails
                      target.style.display = 'none';
                      const placeholder = target.nextElementSibling as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }
                  }}
                />
                <div className={styles.photoPlaceholder} style={{display: 'none'}}>
                  <span className={styles.photoText}>Photo {photo.id}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
    
    <Toastr 
      backgroundColor={isDirty ? '#FFFF1A' : 'white'} 
      message={toastMessage} 
      isVisible={showToast || isDirty} 
      onClose={() => setShowToast(false)} 
    />

    {/* Hidden file input */}
    <input
      type="file"
      accept="image/*"
      ref={fileInputRef}
      style={{ opacity: 0, position: 'absolute' , zIndex: -1}}
      onChange={handleFileChange}
    />

    {/* Camera Preview */}
    {showCamera && (
      <div className={styles.cameraPreview}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          className={styles.cameraVideo}
        />
        <button className={styles.captureButton} onClick={takePhoto}>
          <CameraIcon width={40} height={40} />
        </button>
      </div>
    )}

    {/* Canvas for capturing photo */}
    <canvas ref={canvasRef} style={{ display: "none" }} />

    <ConfirmationModal
      isOpen={showPhotoModal}
      onClose={() => setShowPhotoModal(false)} 
      onConfirm={() => {setShowPhotoModal(false)}} 
      title="Photo Source"
      message=""
      cancelText="CANCEL"
      confirmText="DONE" 
      modalBodyStyle={{padding: '10px 0px 10px 0px'}}
      modalContentStyle={{maxWidth: '400px', width: '100%'}}
      modalHeaderStyle={{padding: 'margin: 0px'}}
      modalButtonStyle={{maxWidth: '400px'}}
      customRender={
        <div className={styles.photoOptions}>
          <div className={`${styles.photoOption} ${styles.galleryOption}`} onClick={handleGallerySelect}>
            Gallery
          </div>
          <div className={styles.photoDivider}></div>
          <div className={`${styles.photoOption} ${styles.cameraOption}`} onClick={handleCameraSelect}>
            Camera
          </div>
        </div>
      }
    />

    {/* Photo Carousel Modal */}
    <Modal
      opened={showCarouselModal}
      onClose={() => setShowCarouselModal(false)}
      size="lg"
      withCloseButton={true}
      styles={{
        body: {
          padding: '0',
        },
        content: {
          position: 'fixed',
          bottom: 0,
          top: 'auto',
          margin: 0,
          maxHeight: '100vh',
          maxWidth: '500px',
          height: 'auto',
          borderTopLeftRadius: '30px',
          borderTopRightRadius: '30px',
        },
        header: {padding: 15},
      }}
      
    >
      <div className={styles.carouselHeader}>
        Check-in Photos
      </div>
      <div className={styles.carouselWrapper}>
          <Carousel
            initialSlide={carouselIndex}
            withIndicators
            withControls={false}
            slideSize="100%"
            slideGap="md"
            onSlideChange={(index) => setCarouselIndex(index)}
            emblaOptions={{ 
              align: 'start', 
              slidesToScroll: 1,
              containScroll: 'trimSnaps',
              loop: false
            }}
            styles={{
              indicators: {
                bottom: -120,
              },
              indicator: {
                width: 10,
                height: 10,
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255)',
                border: 'none',
                '&[data-active]': {
                  backgroundColor: '#000',
                }
              }
            }}
          >
            {mockPhotos.map((photo, index) => (
              <Carousel.Slide key={photo.id}>
                <img 
                  src={photo.dataUrl || photo.url} 
                  alt={`Photo ${photo.id}`} 
                  className={styles.carouselImage}
                  style={{
                    width: '100%',maxHeight: '350px',
                    objectFit: 'contain',
                    objectPosition: 'center'
                  }}
                  onError={(e) => {
                    // Fallback to original URL if data URL fails
                    const target = e.target as HTMLImageElement;
                    if (photo.api_url && target.src !== photo.api_url) {
                      target.src = photo.api_url;
                    }
                  }}
                />
              </Carousel.Slide>
            ))}
          </Carousel>
      </div>
       <div className={styles.deletePhotoButton} onClick={handleDeletePhoto}>
         DELETE PHOTO
       </div>

    </Modal>
  </div>;
};

export default CheckInDetails;
